import { useState } from 'react';
import { Button, Destructive, Icon, Input, Modal, Reason, Switch, useToast } from '@efira/ui';
import { Link } from 'react-router';

import {
  useAccount,
  useAuthConfig,
  useCancelDelete,
  useDeleteAccount,
  useSaveAccount,
  useUnlinkProvider,
} from '../../api/queries';
import { changePassword } from '../../auth/client';

/**
 * Профиль: имя, почта, показ на главной, пароль, входы, удаление.
 *
 * ПОЧТА ТОЛЬКО ДЛЯ ЧТЕНИЯ. Смена адреса - это смена ключа от аккаунта, и
 * делать её полем в общем списке настроек нельзя: она требует подтверждения
 * обоих адресов. Поле объясняет, куда идти, а не молчит.
 *
 * ТРЕБОВАНИЯ К ПАРОЛЮ ПОКАЗАНЫ ДО ОТПРАВКИ. Узнать про восемь символов
 * после отказа - значит потратить попытку на то, что можно было прочитать.
 */
const PROVIDER_NAMES: Record<string, string> = {
  google: 'Google',
  discord: 'Discord',
};

export function Profile() {
  const account = useAccount();

  /*
   * ОДИН ИСТОЧНИК ПРАВДЫ О ПРОВАЙДЕРАХ - НА ОБА МЕСТА.
   *
   * Находка владельца: на экране входа кнопок провайдеров нет (ключей на
   * сервере нет), а в профиле строка «Discord - Подключить» стоит. Два
   * места отвечали на один вопрос из разных источников, и разойтись они
   * могли в любой момент. Теперь и кнопки входа, и строки профиля читают
   * `/api/auth-config`; из профиля берётся только «привязан или нет».
   */
  const config = useAuthConfig();
  const save = useSaveAccount();
  const toast = useToast();

  const data = account.data;
  const [name, setName] = useState<string | null>(null);
  const current = name ?? data?.name ?? '';

  /* Показываются ТОЛЬКО настроенные провайдеры - ровно те, у которых есть
     кнопка на экране входа. */
  const providers = (data?.providers ?? []).filter(
    (provider) => config.data?.[provider.id as 'google' | 'discord'] === true,
  );

  /* Почему «Сохранить» недоступна - словами (правило `UI-10`). */
  const nameWhy =
    current === data?.name
      ? 'Изменений нет — сохранять нечего.'
      : current.trim().length < 2
        ? 'Имя — от двух знаков.'
        : null;

  return (
    <div data-profile="root" className="flex max-w-xl flex-col gap-5">
      <header className="flex flex-col gap-1">
        <h2 className="text-foreground text-xl font-semibold">Профиль</h2>
        {/* Строка-описание: заголовок из одного слова не говорит, что здесь
            можно сделать и чего нельзя. */}
        <p className="text-foreground/80 text-sm">
          Имя, которое видят зрители, доступ к аккаунту и его удаление.
        </p>
      </header>

      {data?.deleteAt !== null && data?.deleteAt !== undefined && (
        <DeleteNotice deleteAt={data.deleteAt} />
      )}

      <h3 className="text-foreground/90 border-foreground/10 border-t pt-4 text-sm font-semibold tracking-wide uppercase">
        Личные данные
      </h3>

      <section data-control-row="profile-name" className="@container flex items-end gap-2">
        <label className="flex min-w-0 flex-1 flex-col gap-1 text-sm">
          Отображаемое имя
          <Input
            data-profile="name"
            value={current}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
        </label>

        {/* Отдельная кнопка сохранения: имя видят зрители, и менять его
            «на лету», по каждой букве, - не то, чего человек ждёт. */}
        <Button
          variant="primary"
          size="s"
          className="shrink-0"
          data-profile="name-save"
          disabled={nameWhy !== null}
          aria-describedby={nameWhy === null ? undefined : 'profile-name-why'}
          onClick={() => {
            save.mutate(
              { name: current },
              {
                onSuccess: () => {
                  toast.show({ tone: 'success', title: 'Имя сохранено' });
                },
              },
            );
          }}
        >
          Сохранить
        </Button>
      </section>

      {nameWhy !== null && <Reason id="profile-name-why">{nameWhy}</Reason>}

      <label className="flex flex-col gap-1 text-sm">
        Почта
        {/* Замок в поле: запрет читается ДО подписи под ним. Одинаковое с
            редактируемым полем оформление заставляло читать, чтобы понять,
            что менять нельзя. */}
        <span className="relative flex items-center">
          <Input data-profile="email" value={data?.email ?? ''} readOnly className="pe-9" />
          <Icon
            name="lock"
            size="sm"
            tone="muted"
            className="pointer-events-none absolute end-3"
            label="Поле только для чтения"
          />
        </span>
        <span className="text-foreground/90 text-xs">
          Адрес — ключ от аккаунта, и меняется он только через поддержку: напишите нам из этого же
          ящика.
        </span>
      </label>

      <h3 className="text-foreground/90 border-foreground/10 border-t pt-4 text-sm font-semibold tracking-wide uppercase">
        Видимость
      </h3>

      {/* Орган - У ПРАВОГО КРАЯ, текст слева: тумблер перед текстом сдвигал
          подписи относительно заголовка раздела, и колонка «ломалась». */}
      <label
        data-profile="feature"
        data-control-row="profile-feature"
        className="@container flex items-center gap-3 text-sm pe-3"
      >
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-foreground text-base font-semibold">
            Показывать меня на главной
          </span>
          <span className="text-foreground/90 text-sm">
            Пока идёт эфир, ваше имя и текущая игра появятся в разделе «Сейчас в эфире» на
            efira.live. Ссылку на TikTok и почту не показываем.
          </span>
        </span>
        <Switch
          data-profile="feature-toggle"
          checked={data?.featured === true}
          onCheckedChange={(next) => {
            save.mutate({ featured: next === true });
          }}
          className="shrink-0"
        />
      </label>

      <h3 className="text-foreground/90 border-foreground/10 border-t pt-4 text-sm font-semibold tracking-wide uppercase">
        Безопасность
      </h3>

      <PasswordForm />

      <section className="border-foreground/10 flex flex-col gap-2 border-t pt-4">
        <h3 className="text-foreground/90 text-sm font-semibold tracking-wide uppercase">
          Способы входа
        </h3>

        {/*
          ОБЪЯСНЕНИЕ «ПРОВАЙДЕРОВ НЕТ» ЖДЁТ ОТВЕТА СЕРВЕРА.
          Тот же дефект, что пустая сетка падов во время загрузки: список
          настроенных провайдеров приходит двумя запросами, и до ответа
          второго он пуст - на полсекунды человек читал, что входа через
          провайдеров на сервере нет, хотя он есть. Ворота согласованности
          ловили это как расхождение профиля с настройкой.
        */}
        {account.isPending || config.isPending ? (
          <p className="text-foreground/80 text-sm" role="status">
            Смотрим, какие способы входа настроены…
          </p>
        ) : providers.length === 0 ? (
          <p data-profile="provider-empty" className="text-foreground/90 text-sm">
            На этом сервере вход только по почте и паролю. Google и Discord подключит владелец —
            тогда они появятся здесь.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {providers.map((provider) => (
              <ProviderRow key={provider.id} id={provider.id} linked={provider.linked} />
            ))}
          </ul>
        )}
      </section>

      <DeleteAccount name={data?.name ?? ''} days={data?.deleteWindowDays ?? 7} />
    </div>
  );
}

function DeleteNotice({ deleteAt }: { deleteAt: string }) {
  const cancel = useCancelDelete();
  const toast = useToast();
  const when = new Date(deleteAt).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });

  return (
    <div
      data-profile="delete-notice"
      className="border-warning/50 bg-warning/10 rounded-lg flex flex-col gap-2 border p-3 text-sm"
    >
      <p className="text-foreground font-semibold">Аккаунт будет удалён {when}</p>
      <p className="text-foreground/90">
        До этого дня всё на месте: игры, звуки, загрузки. После — нет, и восстановить будет нечем.
      </p>
      <Button
        variant="primary"
        size="s"
        className="self-start"
        data-profile="delete-cancel"
        onClick={() => {
          cancel.mutate(undefined, {
            onSuccess: () => {
              toast.show({ tone: 'success', title: 'Удаление отменено' });
            },
          });
        }}
      >
        Отменить удаление
      </Button>
    </div>
  );
}

/** Смена пароля: требования видны ДО отправки. */
function PasswordForm() {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [failure, setFailure] = useState<string | null>(null);

  const short = next.length > 0 && next.length < 8;
  const mismatch = again.length > 0 && again !== next;
  const ready = currentPassword.length > 0 && next.length >= 8 && again === next;
  const passwordWhy =
    currentPassword.length === 0
      ? 'Введите текущий пароль.'
      : next.length < 8
        ? 'Новый пароль — от 8 символов.'
        : again !== next
          ? 'Повторите новый пароль в третьем поле.'
          : null;

  if (!open) {
    return (
      <Button
        variant="secondary"
        size="s"
        className="self-start"
        data-profile="password"
        onClick={() => {
          setOpen(true);
        }}
      >
        Сменить пароль
      </Button>
    );
  }

  return (
    <section data-profile="password-form" className="flex flex-col gap-2">
      <h3 className="text-foreground/90 text-sm font-semibold tracking-wide uppercase">
        Смена пароля
      </h3>

      {/* Требования - ДО отправки, а не в отказе. */}
      <p className="text-foreground/90 text-sm">
        Новый пароль — от 8 символов. Чем длиннее, тем лучше: длина защищает надёжнее, чем
        спецсимволы.
      </p>

      <label className="flex flex-col gap-1 text-sm">
        Текущий пароль
        <Input
          type="password"
          autoComplete="current-password"
          data-profile="password-current"
          value={currentPassword}
          onChange={(event) => {
            setCurrentPassword(event.target.value);
          }}
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        Новый пароль
        <Input
          type="password"
          autoComplete="new-password"
          data-profile="password-new"
          value={next}
          onChange={(event) => {
            setNext(event.target.value);
          }}
        />
        {short && <span className="text-warning text-xs">Нужно хотя бы 8 символов.</span>}
      </label>

      <label className="flex flex-col gap-1 text-sm">
        Ещё раз
        <Input
          type="password"
          autoComplete="new-password"
          data-profile="password-again"
          value={again}
          onChange={(event) => {
            setAgain(event.target.value);
          }}
        />
        {mismatch && <span className="text-warning text-xs">Пароли не совпадают.</span>}
      </label>

      {failure !== null && (
        <p data-profile="password-error" className="text-destructive text-sm">
          {failure}
        </p>
      )}

      <div className="flex gap-2">
        <Button
          variant="primary"
          size="s"
          data-profile="password-save"
          disabled={!ready}
          aria-describedby={passwordWhy === null ? undefined : 'profile-password-why'}
          onClick={() => {
            setFailure(null);

            void changePassword({
              currentPassword,
              newPassword: next,
              revokeOtherSessions: true,
            }).then((result) => {
              if (result.error) {
                setFailure(
                  result.error.message ?? 'Текущий пароль не подошёл. Попробуйте ещё раз.',
                );
                return;
              }

              toast.show({
                tone: 'success',
                title: 'Пароль изменён',
                text: 'Другие устройства вышли из аккаунта.',
              });
              setOpen(false);
              setCurrentPassword('');
              setNext('');
              setAgain('');
            });
          }}
        >
          Сохранить пароль
        </Button>

        <Button
          variant="ghost"
          size="s"
          onClick={() => {
            setOpen(false);
          }}
        >
          Отмена
        </Button>
      </div>

      {passwordWhy !== null && <Reason id="profile-password-why">{passwordWhy}</Reason>}
    </section>
  );
}

function ProviderRow({ id, linked }: { id: string; linked: boolean }) {
  const unlink = useUnlinkProvider();
  const toast = useToast();

  return (
    <li
      data-profile-provider={id}
      data-control-row="profile-provider"
      className="border-foreground/10 rounded-lg flex items-center gap-2 border p-2"
    >
      <Icon name={id === 'google' ? 'globe' : 'chat'} size="sm" className="shrink-0" />
      <span className="text-foreground min-w-0 flex-1 truncate text-sm">
        {PROVIDER_NAMES[id] ?? id}
      </span>

      {linked ? (
        <Destructive
          id="provider-unlink"
          className="shrink-0"
          data={{ 'data-profile-unlink': id }}
          label="Отвязать"
          title={`Отвязать ${PROVIDER_NAMES[id] ?? id}?`}
          confirm="Отвязать"
          onConfirm={() => {
            unlink.mutate(id, {
              onSuccess: () => {
                toast.show({ tone: 'success', title: `${PROVIDER_NAMES[id] ?? id} отвязан` });
              },
              onError: (error: unknown) => {
                /* Причина приходит с сервера: последний способ входа
                   отвязать нельзя, и человек должен прочитать почему. */
                toast.show({
                  tone: 'warning',
                  title: 'Отвязать нельзя',
                  text:
                    (error as { message?: string }).message ??
                    'Это последний способ войти в аккаунт.',
                });
              },
            });
          }}
        >
          Войти через {PROVIDER_NAMES[id] ?? id} больше не получится. Подключить его снова можно
          здесь же.
        </Destructive>
      ) : (
        <Button asChild variant="secondary" size="s" className="shrink-0">
          <a data-profile-link={id} href={`/api/auth/sign-in/social?provider=${id}`}>
            Подключить
          </a>
        </Button>
      )}
    </li>
  );
}

/** Удаление аккаунта: подтверждение вводом имени и окно отмены. */
function DeleteAccount({ name, days }: { name: string; days: number }) {
  const remove = useDeleteAccount();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState('');

  return (
    <section className="border-foreground/10 mt-2 flex flex-col gap-2 border-t pt-4 pb-2">
      <h3 className="text-foreground/90 text-sm font-semibold tracking-wide uppercase">
        Удаление аккаунта
      </h3>

      <p className="text-foreground/90 text-sm">
        Уйдут настройки игр, звуки, музыка и загруженные файлы. Передумать можно в течение {days}{' '}
        дней — до этого дня всё на месте.
      </p>

      <Button
        variant="danger-solid"
        size="s"
        className="self-start"
        data-profile="delete"
        data-destructive="account-delete"
        onClick={() => {
          setOpen(true);
        }}
      >
        Удалить аккаунт
      </Button>

      {open && (
        <Modal
          open
          dirty={confirm !== ''}
          onClose={() => {
            setOpen(false);
          }}
          title="Удалить аккаунт?"
          confirmId="account-delete"
          actions={
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setOpen(false);
                }}
              >
                Отмена
              </Button>
              <Button
                variant="danger-solid"
                data-profile="delete-confirm"
                data-confirm-action
                /* ПУСТОЕ ИМЯ НЕ ПОДТВЕРЖДАЕТ НИЧЕГО. Пока профиль не
                   загрузился, `name` - пустая строка, и сравнение с пустым
                   полем давало РАВЕНСТВО: кнопка удаления аккаунта была
                   активна, не введя ни буквы. */
                disabled={name.trim() === '' || confirm.trim() !== name}
                onClick={() => {
                  remove.mutate(confirm, {
                    onSuccess: () => {
                      toast.show({
                        tone: 'success',
                        title: 'Аккаунт помечен к удалению',
                        text: `Письмо отправлено. Передумаете — отмените здесь же в течение ${days} дней.`,
                      });
                      setOpen(false);
                    },
                  });
                }}
              >
                Удалить
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-2 text-sm">
            <p>
              Чтобы подтвердить, введите имя аккаунта: <strong>{name}</strong>
            </p>
            <Input
              data-profile="delete-input"
              aria-label="Имя аккаунта"
              value={confirm}
              onChange={(event) => {
                setConfirm(event.target.value);
              }}
            />
            <p className="text-foreground/90">
              На почту придёт письмо. Если удаление запросили не вы — отмените его по ссылке из
              письма и смените пароль.
            </p>
            <Link to="/app/settings/billing" className="underline">
              Что будет с оплатой
            </Link>
          </div>
        </Modal>
      )}
    </section>
  );
}
